/**
 * Patient Name Service
 *
 * Handles all patient name-related API operations including
 * fetching IVR-gathered names and updating patient records.
 *
 * @module services/patientNameService
 */

import type { definitions } from '@erebus/api-types';
import type { AxiosRequestConfig } from 'axios';
import { api, erebus } from 'src/boot/axios';
import { IVRGatherField, IVRGatherSummaryResponse } from 'src/models/ivrJourney';

/**
 * Tells the global handler in `boot/axios.js` that the caller explains a 401/403
 * itself, so it must not log the user out. The flag is not in axios's types.
 */
const KEEP_SIGNED_IN = { skipAuthLogout: true } as AxiosRequestConfig;

/**
 * Response from patient name gather endpoint
 */
export interface PatientNameGatherResponse {
  gathers: IVRGatherSummaryResponse[];
}

/**
 * Request payload for updating patient name
 * Matches backend DTO: UpdatePatientNameRequest.java
 */
export interface UpdatePatientNameRequest {
  patientID: number;
  firstName: string;
  surname: string;
}

/** One patient whose name sounds like the recording. */
export type PatientSuggestion = definitions['pkg_ivrJourney.PatientSuggestion'];

export type PatientSuggestionsResponse = definitions['pkg_ivrJourney.PatientSuggestionsResponse'];

/** Request body of POST /api/v3/patients/duplicates/merge (PatientMergeRequest.java). */
export interface PatientMergeRequest {
  patientRecordID: number;
  newPatientID: number;
  oldPatientID: number;
}

/** Response of the merge endpoint (PatientMergeResponse.java). */
export interface PatientMergeResponse {
  patientRecordID: number;
  newPatientID: number;
  oldPatientDeleted: boolean;
  keptReason: string | null;
  alreadySwapped: boolean;
}

/**
 * Service for managing patient name operations
 *
 * Backend Endpoint Requirements:
 * - GET /ivr/journeys/gathers
 *   Query Params: caseID, fields (PATIENT_FULL_NAME)
 *   Returns: { gathers: IVRGatherSummaryResponse[] }
 *   Logic: Query most recent IVR journey for case's activation,
 *          extract PATIENT_FULL_NAME from gather results
 *   Note: Returns first gather from array, or null if empty
 */
export default class PatientNameService {
  /**
   * Fetches several IVR-gathered fields for a case in one request
   *
   * The backend returns the most recent value for each field, so the result is
   * keyed by field name. A field the IVR never captured is simply absent.
   *
   * @param caseNumber - The case/patient record ID
   * @param fields - Which gathered fields to fetch
   * @returns The gather results by field, or an empty object on error
   */
  async getGatherFields(
    caseNumber: number,
    fields: IVRGatherField[],
  ): Promise<Partial<Record<IVRGatherField, IVRGatherSummaryResponse>>> {
    try {
      // The backend reads repeated `fields=` params, not axios's `fields[]=` form.
      const fieldParams = fields.map((field) => `fields=${field}`).join('&');
      const response = await erebus.get<PatientNameGatherResponse>(
        `/ivr/journeys/gathers?caseID=${caseNumber}&${fieldParams}`,
      );
      const byField: Partial<Record<IVRGatherField, IVRGatherSummaryResponse>> = {};
      for (const gather of response.data.gathers ?? []) {
        byField[gather.fieldName] = gather;
      }
      return byField;
    } catch {
      // PIPEDA Compliance: Do not log error details that may contain PHI
      // Backend handles comprehensive logging with PHI filtering
      return {};
    }
  }

  /**
   * Fetches the gathered patient name from the most recent IVR call
   *
   * @param caseNumber - The case/patient record ID
   * @returns The IVR gather result or null if not found or on error
   */
  async getPatientNameFromGather(caseNumber: number): Promise<IVRGatherSummaryResponse | null> {
    const byField = await this.getGatherFields(caseNumber, [IVRGatherField.PATIENT_FULL_NAME]);
    return byField[IVRGatherField.PATIENT_FULL_NAME] ?? null;
  }

  /**
   * Updates a patient's name in the system
   *
   * Uses the V3 patient name update endpoint which provides a dedicated
   * API for updating only the patient's first name and surname.
   *
   * @param request - Patient ID and new name details
   * @throws Error if the update fails
   */
  async updatePatientName(request: UpdatePatientNameRequest): Promise<void> {
    await api.put(`/api/v3/patients/${request.patientID}/name`, request);
  }

  /**
   * Fetches the name recording's transcript and the patients it sounds like
   *
   * @param caseNumber - The case/patient record ID
   * @throws Error if the request fails
   */
  async getPatientSuggestions(caseNumber: number): Promise<PatientSuggestionsResponse> {
    // A refusal hides the matches; it must not log the user out.
    const config = { params: { caseID: caseNumber }, ...KEEP_SIGNED_IN };
    const response = await erebus.get<PatientSuggestionsResponse>(
      '/ivr/journeys/patient-suggestions',
      config,
    );
    return response.data;
  }

  /**
   * Moves a case to an existing patient
   *
   * @param request - The case, the patient it moves to, and the patient it is on now
   * @throws Error if the merge fails. A 409 means the case no longer points at `oldPatientID`.
   */
  async mergePatient(request: PatientMergeRequest): Promise<PatientMergeResponse> {
    const response = await api.post<PatientMergeResponse>(
      '/api/v3/patients/duplicates/merge',
      request,
      // The sheet says why a 403 happened; it must not log the user out.
      KEEP_SIGNED_IN,
    );
    return response.data;
  }
}
